/**
 * Integratie-status — werken alle externe koppelingen nog?
 *
 * Een dagelijkse controle in de AI Engine doet per dienst één goedkope call en
 * bewaart het resultaat. Deze pagina leest dat alleen uit; de knop 'Nu controleren'
 * meet opnieuw.
 *
 * Onderscheid in de status is het punt van deze pagina: een verlopen sleutel of een
 * leeg tegoed vraagt om actie van een beheerder, terwijl storing bij de leverancier
 * meestal vanzelf overgaat. Alles op één hoop gooien maakt de pagina onbruikbaar.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { SettingField } from '../adminSettingsCommon';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';

// Waarvoor elke koppeling in deze app gebruikt wordt. Staat hier en niet in de
// database: het is uitleg bij de tool, geen meetwaarde. Zonder dit moet je bij een
// storing eerst uitzoeken wát er stukgaat voordat je kunt inschatten hoe erg het is.
const WAARVOOR = {
  gripp: 'Offertes, opdrachten, facturen en uren. Voedt de offerte-generator, het sales-dashboard, de forecast en de overdracht.',
  airtable: 'Master voor projecten, taken, personen, draaidagen en deliverables. Vrijwel elke pagina leunt hierop.',
  openai: 'AI-chat, offerte-assistent, taakgeneratie, ondertitel-correctie en de mail-assistent.',
  anthropic: 'AI-chat en het genereren van explainer-scripts.',
  elevenlabs: 'Spraak-naar-tekst (Scribe) voor de recorder, met spreker-herkenning.',
  fal: 'Whisper-transcriptie met woord-timestamps — ondertitels en recorder-opnames.',
  fireflies: 'Meeting-transcripties die in gesprekken en projectdossiers landen.',
  frameio: 'Video-uploads en reviewlinks richting klanten.',
  ms_graph: 'Agenda en beschikbaarheid (draaidagen, planning) en de mailbox van de assistent.',
  outline: 'Wiki-kennis waaruit AI-chat, assistent en offertes putten.',
  database: 'Onze eigen Postgres: spiegels van Airtable en Gripp, dossiers en instellingen.',
};

const STATUS = {
  ok: { label: 'Werkt', color: 'success', uitleg: 'Laatste controle geslaagd.' },
  auth: { label: 'Sleutel ongeldig', color: 'error', uitleg: 'Key verlopen of ingetrokken — vernieuwen bij de leverancier.' },
  quota: { label: 'Tegoed op', color: 'warning', uitleg: 'Krediet op of limiet bereikt — bijvullen.' },
  down: { label: 'Storing', color: 'error', uitleg: 'Dienst onbereikbaar. Gaat meestal vanzelf over.' },
  unknown: { label: 'Niet ingesteld', color: 'default', uitleg: 'Geen sleutel geconfigureerd — deze koppeling is hier niet in gebruik.' },
};

const fmtTijd = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso.endsWith('Z') ? iso : `${iso}Z`);
  return d.toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

/**
 * Aantekening per koppeling — "ticket ingediend bij Gripp, onderhoud t/m vrijdag".
 *
 * Bewust inline en klein: dit is een briefje voor de volgende die kijkt, geen
 * ticketsysteem. Blijft staan als de storing voorbij is, zodat je kunt teruglezen
 * wat het was; leegmaken en opslaan wist hem.
 */
function Aantekening({ item, onOpgeslagen }) {
  const [bewerken, setBewerken] = useState(false);
  const [tekst, setTekst] = useState(item.note || '');
  const [bezig, setBezig] = useState(false);

  const opslaan = async () => {
    setBezig(true);
    try {
      await authFetch(`${BACKEND_URL}/Api/integration-health/${encodeURIComponent(item.name)}/note`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: tekst }),
      });
      setBewerken(false);
      await onOpgeslagen();
    } finally {
      setBezig(false);
    }
  };

  if (!bewerken) {
    return item.note ? (
      <Box onClick={() => setBewerken(true)} sx={{ cursor: 'pointer' }}>
        <Typography variant="body2" sx={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
          {item.note}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {item.noteBy || 'onbekend'}{item.noteAt ? ` · ${fmtTijd(item.noteAt)}` : ''}
        </Typography>
      </Box>
    ) : (
      <Button size="small" variant="text" onClick={() => setBewerken(true)}
        sx={{ textTransform: 'none', fontSize: '0.78rem', color: 'text.secondary', px: 0.5 }}>
        + notitie
      </Button>
    );
  }

  return (
    <Stack spacing={0.5}>
      <TextField
        size="small" fullWidth multiline maxRows={4} autoFocus
        value={tekst} onChange={(e) => setTekst(e.target.value)}
        placeholder="Bijv. ticket ingediend, onderhoud t/m vrijdag"
        inputProps={{ maxLength: 2000 }}
        sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
      />
      <Stack direction="row" spacing={0.5}>
        <Button size="small" variant="contained" onClick={opslaan} disabled={bezig}
          sx={{ textTransform: 'none' }}>
          Opslaan
        </Button>
        <Button size="small" onClick={() => { setTekst(item.note || ''); setBewerken(false); }}
          disabled={bezig} sx={{ textTransform: 'none' }}>
          Annuleer
        </Button>
      </Stack>
    </Stack>
  );
}

export default function IntegratieStatus() {
  const [data, setData] = useState(null);
  const [laden, setLaden] = useState(true);
  const [meten, setMeten] = useState(false);
  const [fout, setFout] = useState(null);

  const ophalen = useCallback(async () => {
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/integration-health`);
      if (!res.ok) throw new Error(`Status ${res.status}`);
      setData(await res.json());
      setFout(null);
    } catch (e) {
      setFout(e.message || 'Kon de status niet ophalen');
    } finally {
      setLaden(false);
    }
  }, []);

  useEffect(() => { ophalen(); }, [ophalen]);

  const nuMeten = async () => {
    setMeten(true);
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/integration-health/run`, { method: 'POST' });
      if (!res.ok) throw new Error(`Status ${res.status}`);
      await ophalen();
    } catch (e) {
      setFout(e.message || 'Controle mislukt');
    } finally {
      setMeten(false);
    }
  };

  if (laden) return <Box sx={{ p: 3 }}><CircularProgress size={24} /></Box>;

  const items = data?.integrations || [];
  const actieNodig = items.filter((i) => i.status === 'auth' || i.status === 'quota');
  const storing = items.filter((i) => i.status === 'down');

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Integratie-status</Typography>
          <Typography variant="body2" color="text.secondary">
            Dagelijks om 07:00 gecontroleerd · laatste meting {fmtTijd(data?.lastCheckedAt)}
          </Typography>
        </Box>
        <Button variant="outlined" size="small" onClick={nuMeten} disabled={meten}
          startIcon={meten ? <CircularProgress size={14} /> : <RefreshIcon />}>
          Nu controleren
        </Button>
      </Stack>

      {fout && <Alert severity="error">{fout}</Alert>}

      {actieNodig.length > 0 && (
        <Alert severity="error">
          <b>{actieNodig.length === 1 ? 'Eén koppeling vraagt' : `${actieNodig.length} koppelingen vragen`} om actie.</b>{' '}
          {actieNodig.map((i) => i.label).join(', ')} — een verlopen sleutel of een leeg tegoed
          lost zichzelf niet op.
        </Alert>
      )}
      {actieNodig.length === 0 && storing.length > 0 && (
        <Alert severity="warning">
          {storing.map((i) => i.label).join(', ')} {storing.length === 1 ? 'is' : 'zijn'} nu niet
          bereikbaar. Ligt bij de leverancier — meestal tijdelijk.
        </Alert>
      )}
      {actieNodig.length === 0 && storing.length === 0 && items.length > 0 && (
        <Alert severity="success">Alle gecontroleerde koppelingen werken.</Alert>
      )}
      {items.length === 0 && (
        <Alert severity="info">
          Nog niets gemeten. Klik op ‘Nu controleren’ of wacht op de dagelijkse run.
        </Alert>
      )}

      {items.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Koppeling &amp; waarvoor</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Sinds</TableCell>
              <TableCell>Snelheid</TableCell>
              <TableCell>Details</TableCell>
              <TableCell>Aantekening</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((i) => {
              const s = STATUS[i.status] || STATUS.unknown;
              return (
                <TableRow key={i.name} hover>
                  <TableCell sx={{ maxWidth: 260 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{i.label}</Typography>
                    {WAARVOOR[i.name] && (
                      <Typography variant="caption" color="text.secondary"
                        sx={{ display: 'block', lineHeight: 1.35, mt: 0.25 }}>
                        {WAARVOOR[i.name]}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={s.uitleg}>
                      <Chip size="small" label={s.label} color={s.color}
                        variant={i.status === 'ok' ? 'outlined' : 'filled'} />
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: '0.8rem' }}>
                    {i.status === 'ok' ? '—' : fmtTijd(i.statusSince)}
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                    {i.latencyMs ? `${i.latencyMs} ms` : '—'}
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem', maxWidth: 380 }}>
                    {Number.isFinite(i.quotaLimit) && Number.isFinite(i.quotaUsed)
                      ? `${i.quotaUsed.toLocaleString('nl-NL')} / ${i.quotaLimit.toLocaleString('nl-NL')} verbruikt`
                      : (i.error || '—')}
                  </TableCell>
                  <TableCell sx={{ minWidth: 240 }}>
                    <Aantekening item={i} onOpgeslagen={ophalen} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <Box sx={{ pt: 1 }}>
        <SettingField
          settingKey="integration_health_admins"
          label="Wie krijgt een melding?"
          helperText={
            'Komma-gescheiden e-mailadressen. Melding gaat alleen bij een verandering '
            + '— gaat een koppeling stuk of werkt hij weer — niet bij elke dagelijkse '
            + 'herhaling. Leeg betekent: alleen loggen, niemand krijgt bericht.'
          }
        />
      </Box>

      <Alert severity="info" icon={false} sx={{ fontSize: '0.8rem' }}>
        <b>Let op: dit signaleert een leeg tegoed pas als het op is.</b> Deze pagina
        meet of een koppeling wérkt — bij OpenAI en Anthropic met een generatie van
        één token, dus een leeg krediet valt meteen op. Maar een waarschuwing vooraf
        (“nog 10% over”) regel je bij de leverancier zelf: stel daar een drempel en
        e-mailalert in, eventueel met automatisch bijladen. Bewuste keuze — zo krijg
        je dat signaal ook als deze app zelf plat ligt.
      </Alert>
    </Stack>
  );
}
